import { useLocale } from "./useLocale";
import { msg } from "../locales/translate";
import { useCallback, useEffect, useState } from "react";
import { GD } from "src/GD";
import type { IClient } from "src/services/ClientService";
import type { IStatementRow } from "src/types/statement";

type StatementLoadState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| { status: "ready"; statement: IStatementRow; client: IClient; response: IAPIResponse<IStatementRow> };

interface StatementLoadResult {
	statementID: number;
	version: number;
	state: StatementLoadState;
}

export const useStatementDetails = (statementID: number) => {
	useLocale();
	const [version, setVersion] = useState(0);
	const [result, setResult] = useState<StatementLoadResult>();
	const reload = useCallback(() => setVersion(value => value + 1), []);

	useEffect(() => {
		let cancelled = false;
		const publish = (state: StatementLoadState) => {
			if (!cancelled) setResult({ statementID, version, state });
		};

		const load = async () => {
			try {
				const response: IAPIResponse<IStatementRow> = await GD.REQ_API.request({ method: "statements/get", data: { id: statementID } });
				if (cancelled) return;
				if (response.error || !response.data || response.data.id !== statementID) {
					publish({ status: "error", message: response.error?.message ?? msg("unableToLoadStatement") });
					return;
				}

				const clientResponse = await GD.REQ_CLIENT.request({ clientID: response.data.client_id });
				if (cancelled) return;
				if (clientResponse?.error || !clientResponse?.data) {
					publish({ status: "error", message: clientResponse?.error?.message ?? msg("unableToLoadStatementClient") });
					return;
				}

				publish({ status: "ready", statement: response.data, client: clientResponse.data, response });
			} catch {
				publish({ status: "error", message: msg("unableToLoadStatementPleaseTryAgain") });
			}
		};

		void load();
		return () => { cancelled = true; };
	}, [statementID, version]);

	const state: StatementLoadState = result?.statementID === statementID && result.version === version ? result.state : { status: "loading" };
	return { state, reload };
};
